import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  useWindowDimensions,
  Linking,
  Image,
} from 'react-native';
import { useNavigation, useRoute, RouteProp, useFocusEffect } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path, Circle } from 'react-native-svg';
import { formatPct } from '../../utils/formatPercentage';
import { COLORS, FONTS, API_BASE_HOST } from '../../constants/config';
import { commitmentColor } from '../../utils/metricColors';
import { PageContainer, Navbar, Breadcrumb, EvidenceTable } from '../../components';
import TargetAchievementCard from '../../components/TargetAchievementCard';
import { useResponsive, useRoleNavigation, useAutoTranslate } from '../../hooks';
import { isPercentUnit } from '../../utils/targetGrid';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { subProcessesApi, processesApi } from '../../services/apiService';
import { SubProcess, KPIList, Process, ProcessStep } from '../../types';
import DeletionImpactModal from '../../components/DeletionImpactModal';
import DeletionApprovalBanner from '../../components/DeletionApprovalBanner';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTextAlign } from '../../utils/rtl';
import { useYear } from '../../contexts/YearContext';

import { tokens } from '@design';
import { Button } from '@design/components/Button';
import { PageHeader } from '@design/components/PageHeader';
type RouteProps = RouteProp<MainStackParamList, 'SubProcessDetail'>;
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// Escapes entity-derived values before interpolating them into the print-export
// HTML string (handleExportPDF below) to prevent HTML/script injection from
// user-controlled fields (step/kpi/subProcess names, descriptions, etc.).
const escapeHtml = (v: unknown): string => String(v ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

// ============================================================
// SVG Icon Components
// ============================================================

const ChevronLeftIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const EditIcon = ({ size = 16, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M18.5 2.50001C18.8978 2.10219 19.4374 1.87869 20 1.87869C20.5626 1.87869 21.1022 2.10219 21.5 2.50001C21.8978 2.89784 22.1213 3.4374 22.1213 4.00001C22.1213 4.56262 21.8978 5.10219 21.5 5.50001L12 15L8 16L9 12L18.5 2.50001Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ExportIcon = ({ size = 16, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M17 8L12 3L7 8" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 3V15" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ClockIcon = ({ size = 16, color = tokens.colors.raw.slate["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 6V12L16 14" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const UserIcon = ({ size = 16, color = tokens.colors.raw.slate["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const UsersIcon = ({ size = 16, color = tokens.colors.raw.slate["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const BuildingIcon = ({ size = 16, color = tokens.colors.raw.slate["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M3 21H21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M5 21V7L13 3V21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M19 21V11L13 7" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M9 9V9.01" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M9 12V12.01" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M9 15V15.01" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M9 18V18.01" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const PlusIcon = ({ size = 16, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 5V19M5 12H19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const FileIcon = ({ size = 16, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M14 2V8H20" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// ============================================================
// Donut Chart Component
// ============================================================

interface DonutChartProps {
  percentage: number;
  size?: number;
  strokeWidth?: number;
}

/**
 * Get color based on achievement percentage
 * - >= 100%: Green (#22C55E) - ممتاز
 * - >= 80% and < 100%: Orange (#F97316) - جيد
 * - < 80%: Red (#EF4444) - يحتاج تحسين
 */
const getAchievementColor = (percentage: number): string => {
  if (percentage >= 100) return tokens.colors.raw.green["500"];  // Green - Excellent
  if (percentage >= 80) return tokens.colors.raw.orange["500"];   // Orange - Good
  return tokens.colors.raw.red["500"];                          // Red - Needs Improvement
};

const DonutChart = ({ percentage, size = 168, strokeWidth = 12 }: DonutChartProps) => {
  const { t } = useLanguage();
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  const center = size / 2;

  // Dynamic color based on achievement percentage
  const progressColor = commitmentColor(percentage);

  // Calculate dot position at END of progress arc
  // -90 offset to start from top (12 o'clock position)
  const angle = ((percentage / 100) * 360 - 90) * (Math.PI / 180);
  const dotX = center + radius * Math.cos(angle);
  const dotY = center + radius * Math.sin(angle);

  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size}>
        {/* Background circle */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={tokens.colors.raw.slate["200"]}
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress arc - rotation applied to this element only */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={progressColor}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${center} ${center})`}
        />
        {/* Dot at end of progress - ONLY show if percentage > 0 */}
        {percentage > 0 && (
          <Circle
            cx={dotX}
            cy={dotY}
            r={strokeWidth / 2 + 2}
            fill={progressColor}
            stroke="#FCFCFC"
            strokeWidth={2}
          />
        )}
      </Svg>
      <View style={{ position: 'absolute', alignItems: 'center' }}>
        <Text style={{ fontSize: 32, fontFamily: FONTS.numbers, fontWeight: '500', color: tokens.colors.semantic.general.foreground }}>
          %{formatPct(percentage)}
        </Text>
        <Text style={{ fontSize: tokens.typography.paragraph["paragraph small font size"], fontFamily: FONTS.regular, color: tokens.colors.raw.slate["500"] }}>
          {t('kpis.commitmentRate')}
        </Text>
      </View>
    </View>
  );
};

// ============================================================
// Main Component
// ============================================================

export default function SubProcessDetailScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const { id } = route.params;
  const { width } = useWindowDimensions();
  const { horizontalCardWidth, isDesktop } = useResponsive();
  // Stack on tablet + mobile (< 1024px) — tablet too narrow for desktop grid.
  const isSmallScreen = !isDesktop;
  const { navigateToHome, navigateToProcessDetail } = useRoleNavigation();
  const { showError } = useNotification();
  const { user } = useAuth();
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const styles = useMemo(() => getStyles(isRTL), [isRTL]);
  const { translate, translateOrg } = useAutoTranslate();
  const { selectedYear } = useYear();

  // Theme colors for dark mode
  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textSecondary: colors.textSecondary,
    textMuted: colors.textMuted,
    iconDefault: colors.iconDefault,
    border: colors.border,
    backgroundAlt: isDark ? tokens.colors.raw.gray["700"] : tokens.colors.raw.gray["100"],
    sectionBg: isDark ? tokens.colors.raw.gray["700"] : tokens.colors.raw.slate["100"],
  };

  const [subProcess, setSubProcess] = useState<SubProcess | null>(null);
  const [parentProcess, setParentProcess] = useState<Process | null>(null);
  const [kpis, setKpis] = useState<KPIList[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDeletionImpact, setShowDeletionImpact] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const isOrgEmployee = user?.profile?.user_type === 'org_employee';
  const canApproveDeletion = ['org_executive', 'strategy_team', 'superuser'].includes(user?.profile?.user_type || '');
  const canManageEvidence = ['org_employee', 'coordinator', 'superuser'].includes(user?.profile?.user_type || '');
  const canViewAllEvidence = ['strategy_team', 'executive', 'performance_evaluator', 'evaluation_approver', 'superuser'].includes(user?.profile?.user_type || '');

  // Steps from API (subProcess.steps)
  const steps: ProcessStep[] = subProcess?.steps || [];

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const subProcessData = await subProcessesApi.get(id);

      setSubProcess(subProcessData);
      // Use KPIs from main response instead of separate API call
      setKpis(subProcessData.kpis || []);

      // Fetch parent process data
      if (subProcessData.process) {
        try {
          const parentData = await processesApi.get(subProcessData.process);
          setParentProcess(parentData);
        } catch {
          // Parent process fetch failed, continue without it
        }
      }
    } catch (err) {
      showError(t('subProcesses.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [id, showError]);

  useFocusEffect(
    useCallback(() => {
      fetchData();
    }, [fetchData])
  );

  // Refetch when year changes
  useEffect(() => {
    fetchData();
  }, [selectedYear]);

  const handleEdit = () => {
    if (subProcess) {
      navigation.navigate('AddSubProcess', {
        processId: subProcess.process,
        subProcessId: subProcess.id
      });
    }
  };

  const handleDelete = () => {
    setShowDeletionImpact(true);
  };

  const confirmDelete = async () => {
    try {
      setIsDeleting(true);
      const response = await subProcessesApi.delete(id);
      if ((response as any)?.status === 202) {
        showSuccess(t('deletion.requestSent'));
      } else {
        showSuccess(t('subProcesses.deleteSuccess'));
      }
      navigation.goBack();
    } catch (error: any) {
      if (error.response?.status === 202) {
        showSuccess(t('deletion.requestSent'));
        navigation.goBack();
      } else if (error.response?.status === 409) {
        showError(t('deletion.alreadyPending'));
      } else {
        showError(t('messages.deleteFailed'));
      }
    } finally {
      setIsDeleting(false);
      setShowDeletionImpact(false);
    }
  };

  const handleExportPDF = () => {
    if (!subProcess) {
      showError(t('common.noDataToExport'));
      return;
    }

    // Create printable HTML content
    const stepsHTML = steps.length > 0
      ? steps.map((step, index) => `
          <tr>
            <td style="padding: 12px; border: 1px solid #E5E7EB; text-align: right;">${escapeHtml(step.tools) || '-'}</td>
            <td style="padding: 12px; border: 1px solid #E5E7EB; text-align: right;">${escapeHtml(step.duration) || '-'}</td>
            <td style="padding: 12px; border: 1px solid #E5E7EB; text-align: right;">${escapeHtml(step.responsible) || '-'}</td>
            <td style="padding: 12px; border: 1px solid #E5E7EB; text-align: right;">${escapeHtml(step.description) || '-'}</td>
            <td style="padding: 12px; border: 1px solid #E5E7EB; text-align: right; font-weight: 600;">${escapeHtml(step.name)}</td>
            <td style="padding: 12px; border: 1px solid #E5E7EB; text-align: center; font-weight: 600;">${index + 1}</td>
          </tr>
        `).join('')
      : `<tr><td colspan="6" style="padding: 20px; text-align: center; color: #6B7280;">${t('subProcesses.noSteps')}</td></tr>`;

    const kpisHTML = kpis.length > 0
      ? kpis.map(kpi => `
          <tr>
            <td style="padding: 12px; border: 1px solid #E5E7EB; text-align: center;">${kpi.commitment_rate != null ? `${escapeHtml(kpi.commitment_rate)}%` : '—'}</td>
            <td style="padding: 12px; border: 1px solid #E5E7EB; text-align: right;">${escapeHtml(kpi.target_value) || '-'}</td>
            <td style="padding: 12px; border: 1px solid #E5E7EB; text-align: right;">${escapeHtml(kpi.measurement_frequency) || '-'}</td>
            <td style="padding: 12px; border: 1px solid #E5E7EB; text-align: right;">${escapeHtml(kpi.name)}</td>
          </tr>
        `).join('')
      : `<tr><td colspan="4" style="padding: 20px; text-align: center; color: #6B7280;">${t('kpis.noKPIs')}</td></tr>`;

    const htmlContent = `
      <!DOCTYPE html>
      <html dir="rtl" lang="ar">
      <head>
        <meta charset="UTF-8">
        <title>${t('processes.subProcessDetails')} - ${escapeHtml(subProcess.name)}</title>
        <style>
          * { box-sizing: border-box; margin: tokens.spacing.absolute["0"]; padding: tokens.spacing.absolute["0"]; }
          body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            padding: 40px;
            background: #fff;
            color: #1F2937;
            direction: rtl;
          }
          .header {
            text-align: center;
            margin-bottom: 30px;
            padding-bottom: 20px;
            border-bottom: 2px solid #2D368F;
          }
          .header h1 {
            color: #2D368F;
            font-size: 24px;
            margin-bottom: 8px;
          }
          .header p {
            color: #6B7280;
            font-size: 14px;
          }
          .section {
            margin-bottom: 30px;
          }
          .section-title {
            font-size: 18px;
            font-weight: 600;
            color: #1F2937;
            margin-bottom: 16px;
            padding-bottom: 8px;
            border-bottom: 1px solid #E5E7EB;
          }
          .info-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 16px;
            margin-bottom: 20px;
          }
          .info-item {
            background: #F9FAFB;
            padding: 16px;
            border-radius: 8px;
            border: 1px solid #E5E7EB;
          }
          .info-label {
            font-size: 12px;
            color: #6B7280;
            margin-bottom: 4px;
          }
          .info-value {
            font-size: 16px;
            font-weight: 600;
            color: #1F2937;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 12px;
          }
          th {
            background: #F3F4F6;
            padding: 12px;
            border: 1px solid #E5E7EB;
            text-align: right;
            font-weight: 600;
            color: #374151;
          }
          .description {
            background: #F9FAFB;
            padding: 16px;
            border-radius: 8px;
            border: 1px solid #E5E7EB;
            line-height: 1.8;
          }
          .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #E5E7EB;
            text-align: center;
            color: #9CA3AF;
            font-size: 12px;
          }
          @media print {
            body { padding: 20px; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${t('processes.subProcessDetails')}</h1>
          <p>${escapeHtml(subProcess.name)}</p>
        </div>

        <div class="section">
          <h2 class="section-title">${t('common.basicInfo')}</h2>
          <div class="info-grid">
            <div class="info-item">
              <div class="info-label">${t('processes.subProcessName')}</div>
              <div class="info-value">${escapeHtml(subProcess.name)}</div>
            </div>
            <div class="info-item">
              <div class="info-label">${t('subProcesses.mainProcess')}</div>
              <div class="info-value">${escapeHtml(parentProcess?.name) || '-'}</div>
            </div>
            <div class="info-item">
              <div class="info-label">${t('initiatives.implementationResponsibility')}</div>
              <div class="info-value">${(subProcess.organizational_units_details || []).map(u => escapeHtml(u.name_ar || u.name)).join('، ') || '-'}</div>
            </div>
            <div class="info-item">
              <div class="info-label">${t('processes.processTime')}</div>
              <div class="info-value">${escapeHtml(subProcess.duration_display) || '-'}</div>
            </div>
            <div class="info-item">
              <div class="info-label">${t('processes.employeesCount')}</div>
              <div class="info-value">${escapeHtml(subProcess.employees_count || 0)}</div>
            </div>
            <div class="info-item">
              <div class="info-label">${t('processes.executionFrequency')}</div>
              <div class="info-value">${escapeHtml(subProcess.execution_frequency) || '-'}</div>
            </div>
          </div>
        </div>

        ${subProcess.description ? `
        <div class="section">
          <h2 class="section-title">${t('common.description')}</h2>
          <div class="description">${escapeHtml(subProcess.description)}</div>
        </div>
        ` : ''}

        <div class="section">
          <h2 class="section-title">${t('subProcesses.processSteps')} (${steps.length})</h2>
          <table>
            <thead>
              <tr>
                <th>${t('processes.tools')}</th>
                <th>${t('processes.duration')}</th>
                <th>${t('processes.responsible')}</th>
                <th>${t('common.description')}</th>
                <th>${t('processes.stepName')}</th>
                <th style="width: 50px;">${t('processes.stepOrder')}</th>
              </tr>
            </thead>
            <tbody>
              ${stepsHTML}
            </tbody>
          </table>
        </div>

        <div class="section">
          <h2 class="section-title">${t('kpis.performanceIndicators')} (${kpis.length})</h2>
          <table>
            <thead>
              <tr>
                <th style="width: 100px;">${t('kpis.commitmentRate')}</th>
                <th>${t('kpis.targetValue')}</th>
                <th>${t('kpis.measurementFrequency')}</th>
                <th>${t('kpis.kpiName')}</th>
              </tr>
            </thead>
            <tbody>
              ${kpisHTML}
            </tbody>
          </table>
        </div>

        <div class="footer">
          <p>${t('common.exportedOn')}: ${new Date().toLocaleDateString('ar-SA', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          <p>${t('common.appName')}</p>
        </div>
      </body>
      </html>
    `;

    // Open print window
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      printWindow.focus();

      // Wait for content to load then print
      setTimeout(() => {
        printWindow.print();
      }, 250);
    } else {
      showError(t('common.printWindowError'));
    }
  };

  const handleAddKPI = () => {
    if (subProcess) {
      // Navigate to SubProcess Edit page with scrollToKpi flag
      navigation.navigate('AddSubProcess', {
        processId: subProcess.process,
        subProcessId: subProcess.id,
        scrollToKpi: true
      });
    }
  };

  const handleViewKPIDetails = (kpiId: number) => {
    navigation.navigate('KPIDetail', { id: kpiId });
  };

  const handleGoBack = () => {
    navigation.goBack();
  };

  const handleGoToParentProcess = () => {
    if (subProcess?.process) {
      navigation.navigate('ProcessDetail', { id: subProcess.process });
    }
  };

  // Main donut shows the sub-process commitment rate (engine value, null → "--")
  const commitmentRate = subProcess?.commitment_rate ?? null;

  if (loading) {
    return (
      <PageContainer>
        <Navbar />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={COLORS.primary} />
          <Text style={[styles.loadingText, { color: colors.textMuted }]}>{t('common.loading')}</Text>
        </View>
      </PageContainer>
    );
  }

  if (!subProcess) {
    return (
      <PageContainer>
        <Navbar />
        <View style={styles.errorContainer}>
          <Text style={[styles.errorText, { color: colors.textMuted }]}>{t('subProcesses.notFound')}</Text>
          <TouchableOpacity style={styles.backButton} onPress={handleGoBack}>
            <Text style={styles.backButtonText}>{t('common.back')}</Text>
          </TouchableOpacity>
        </View>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Navbar />
      <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
        <View style={styles.content}>
          {/* Breadcrumb */}
          <Breadcrumb
            items={[
              { label: t("navigation.home") },
              { label: t("navigation.processes"), route: "Processes" },
              {
                label: t("processes.processDetails"),
                route: "ProcessDetail",
                params: { id: subProcess?.process },
              },
              { label: t("subProcesses.title") },
            ]}
          />

          <PageHeader
            title={translate(subProcess.name)}
            description={translate(subProcess.description) || t("common.noDescription")}
            actions={
              <View style={styles.headerActions}>
                <Button
                  variant="secondary"
                  onPress={handleExportPDF}
                  trailingIcon={<ExportIcon size={16} color={themeColors.iconDefault} />}
                >
                  {t("common.exportPDF")}
                </Button>
                <Button variant="destructive" onPress={handleDelete}>
                  {t("common.delete")}
                </Button>
                <Button
                  variant="secondary"
                  onPress={handleEdit}
                  trailingIcon={<EditIcon size={16} color={themeColors.iconDefault} />}
                >
                  {t("common.edit")}
                </Button>
              </View>
            }
          />

          {/* Pending Deletion Banner */}
          {(subProcess as any).pending_deletion_info && (
            <DeletionApprovalBanner
              deletionInfo={(subProcess as any).pending_deletion_info}
              canApprove={canApproveDeletion}
              canCancel={isOrgEmployee && (subProcess as any).pending_deletion_info.requested_by_id === user?.id}
              onApproved={() => { showSuccess(t('deletion.approved')); navigation.goBack(); }}
              onRejected={() => { showSuccess(t('deletion.rejected')); fetchData(); }}
              onCancelled={() => { showSuccess(t('deletion.requestCancelled')); fetchData(); }}
              onViewImpact={() => setShowDeletionImpact(true)}
            />
          )}

          {/* Main Content - Donut Chart and Basic Info */}
          <View
            style={[
              styles.mainContentRow,
              isSmallScreen && styles.mainContentRowSmall,
            ]}
          >
            {/* Right - Basic Information */}
            <View
              style={[
                styles.basicInfoSection,
                {
                  backgroundColor: themeColors.card,
                  borderColor: themeColors.cardBorder,
                },
                isSmallScreen && styles.basicInfoSectionSmall,
              ]}
            >
              <Text
                style={[
                  styles.basicInfoTitle,
                  { color: themeColors.textPrimary },
                ]}
              >
                {t("common.basicInfo")}
              </Text>

              <View style={styles.infoGrid}>
                <View style={styles.infoRow}>
                  <View
                    style={[
                      styles.infoItem,
                      { backgroundColor: themeColors.sectionBg },
                    ]}
                  >
                    <Text
                      style={[
                        styles.infoLabel,
                        { color: themeColors.textMuted },
                      ]}
                    >
                      {t("subProcesses.mainProcess")}
                    </Text>
                    <Text
                      style={[
                        styles.infoValue,
                        { color: themeColors.textPrimary },
                      ]}
                    >
                      {translate(parentProcess?.name) || "-"}
                    </Text>
                  </View>
                  <View
                    style={[
                      styles.infoItem,
                      { backgroundColor: themeColors.sectionBg },
                    ]}
                  >
                    <Text
                      style={[
                        styles.infoLabel,
                        { color: themeColors.textMuted },
                      ]}
                    >
                      {t("initiatives.implementationResponsibility")}
                    </Text>
                    <Text
                      style={[
                        styles.infoValue,
                        { color: themeColors.textPrimary },
                      ]}
                    >
                      {(subProcess.organizational_units_details || []).map(u => translate(u.name_ar || u.name)).join('، ') || "-"}
                    </Text>
                  </View>
                </View>

                <View style={styles.infoRow}>
                  <View
                    style={[
                      styles.infoItem,
                      { backgroundColor: themeColors.sectionBg },
                    ]}
                  >
                    <Text
                      style={[
                        styles.infoLabel,
                        { color: themeColors.textMuted },
                      ]}
                    >
                      {t("common.classification")}
                    </Text>
                    <Text
                      style={[
                        styles.infoValue,
                        { color: themeColors.textPrimary },
                      ]}
                    >
                      {t("subProcesses.sub")}
                    </Text>
                  </View>
                  <View
                    style={[
                      styles.infoItem,
                      { backgroundColor: themeColors.sectionBg },
                    ]}
                  >
                    <Text
                      style={[
                        styles.infoLabel,
                        { color: themeColors.textMuted },
                      ]}
                    >
                      {t("subProcesses.processTime")}
                    </Text>
                    <Text
                      style={[
                        styles.infoValue,
                        { color: themeColors.textPrimary },
                      ]}
                    >
                      {subProcess.duration_display || "-"}
                    </Text>
                  </View>
                </View>
              </View>
            </View>

            {/* Left - Donut Chart */}
            <View
              style={[
                styles.donutSection,
                {
                  backgroundColor: themeColors.card,
                  borderColor: themeColors.cardBorder,
                },
                isSmallScreen && styles.donutSectionSmall,
              ]}
            >
              <Text
                style={[
                  styles.sectionLabel,
                  { color: themeColors.textPrimary },
                ]}
              >
                {t("kpis.performanceIndicators")}
              </Text>
              {commitmentRate != null ? (
                <DonutChart
                  percentage={commitmentRate}
                  size={168}
                  strokeWidth={14}
                />
              ) : (
                <View style={{ width: 168, height: 168, justifyContent: 'center', alignItems: 'center' }}>
                  <Text style={{ fontSize: 36, fontWeight: '700', color: themeColors.textMuted }}>--</Text>
                </View>
              )}
              {/* Legend Items */}
              <View style={styles.donutLegendItems}>
                <View style={styles.donutLegendRow}>
                  <View
                    style={[
                      styles.donutLegendDot,
                      { backgroundColor: tokens.colors.raw.green["500"] },
                    ]}
                  />
                  <Text
                    style={[
                      styles.donutLegendLabel,
                      { color: themeColors.textMuted },
                    ]}
                  >
                    {t("kpis.excellent")} (≥100%)
                  </Text>
                </View>
                <View style={styles.donutLegendRow}>
                  <View
                    style={[
                      styles.donutLegendDot,
                      { backgroundColor: tokens.colors.raw.orange["500"] },
                    ]}
                  />
                  <Text
                    style={[
                      styles.donutLegendLabel,
                      { color: themeColors.textMuted },
                    ]}
                  >
                    {t("kpis.good")} (≥80%)
                  </Text>
                </View>
                <View style={styles.donutLegendRow}>
                  <View
                    style={[
                      styles.donutLegendDot,
                      { backgroundColor: tokens.colors.raw.red["500"] },
                    ]}
                  />
                  <Text
                    style={[
                      styles.donutLegendLabel,
                      { color: themeColors.textMuted },
                    ]}
                  >
                    {t("kpis.needsImprovement")}
                  </Text>
                </View>
              </View>
            </View>
          </View>

          {/* KPIs Section */}
          <View
            style={[
              styles.kpisSection,
              {
                backgroundColor: themeColors.card,
                borderColor: themeColors.cardBorder,
              },
            ]}
          >
            <View style={styles.kpisSectionHeader}>
              <TouchableOpacity
                style={[styles.addKpiBtn, { borderColor: themeColors.border }]}
                onPress={handleAddKPI}
              >
                <Text
                  style={[
                    styles.addKpiBtnText,
                    { color: themeColors.textPrimary },
                  ]}
                >
                  {t("kpis.addKPI")}
                </Text>
                <PlusIcon size={13} color={themeColors.iconDefault} />
              </TouchableOpacity>
              <Text
                style={[
                  styles.kpisSectionTitle,
                  { color: themeColors.textPrimary },
                ]}
              >
                {t("kpis.performanceIndicators")}
              </Text>
            </View>

            <View style={styles.rtlWrapper}>
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={true}
                contentContainerStyle={styles.horizontalCardsRow}
                style={styles.horizontalScrollContainer}
              >
                {kpis.length > 0 ? (
                  kpis.map((kpi) => {
                    // Commitment rate (نسبة الالتزام); null = nothing due yet → card renders —
                    const commitment = kpi.commitment_rate ?? null;
                    const progressColor = commitmentColor(commitment);

                    return (
                      <View
                        key={kpi.id}
                        style={[styles.rtlCard, { width: horizontalCardWidth }]}
                      >
                        <TargetAchievementCard
                          title={translate(kpi.name)}
                          achieved={kpi.latest_value || '0'}
                          target={kpi.target_value}
                          unit={isPercentUnit(kpi.unit) ? '%' : translate(kpi.unit)}
                          achievedColor={progressColor}
                          percentage={commitment}
                          onViewDetails={() => handleViewKPIDetails(kpi.id)}
                        />
                      </View>
                    );
                  })
                ) : (
                  <View style={[styles.emptyStateContainer, styles.rtlCard]}>
                    <Text
                      style={[
                        styles.emptyStateText,
                        { color: themeColors.textMuted },
                      ]}
                    >
                      {t("kpis.noKPIs")}
                    </Text>
                    <TouchableOpacity
                      style={[
                        styles.addKpiEmptyBtn,
                        { borderColor: themeColors.border },
                      ]}
                      onPress={handleAddKPI}
                    >
                      <Text
                        style={[
                          styles.addKpiEmptyBtnText,
                          { color: themeColors.textPrimary },
                        ]}
                      >
                        {t("kpis.addKPI")}
                      </Text>
                      <PlusIcon size={13} color={themeColors.iconDefault} />
                    </TouchableOpacity>
                  </View>
                )}
              </ScrollView>
            </View>
          </View>

          {/* Resources and Execution Section - الموارد والتنفيذ */}
          <View
            style={[
              styles.resourcesSection,
              {
                backgroundColor: themeColors.card,
                borderColor: themeColors.cardBorder,
              },
            ]}
          >
            <Text
              style={[
                styles.resourcesSectionTitle,
                { color: themeColors.textPrimary },
              ]}
            >
              {t("subProcesses.resourcesAndExecution")}
            </Text>
            <View style={styles.resourcesGrid}>
              <View
                style={[
                  styles.resourcesRow,
                  isSmallScreen && styles.resourcesRowSmall,
                ]}
              >
                <View
                  style={[
                    styles.resourceItem,
                    { backgroundColor: themeColors.sectionBg },
                  ]}
                >
                  <Text
                    style={[
                      styles.resourceLabel,
                      { color: themeColors.textMuted },
                    ]}
                  >
                    {t("processes.executionFrequency")}
                  </Text>
                  <Text
                    style={[
                      styles.resourceValue,
                      { color: themeColors.textPrimary },
                    ]}
                  >
                    {subProcess.execution_frequency
                      ? { daily: t('kpis.daily'), weekly: t('kpis.weekly'), monthly: t('kpis.monthly'), quarterly: t('kpis.quarterly'), half_yearly: t('kpis.semiAnnual'), yearly: t('kpis.annual') }[subProcess.execution_frequency] || subProcess.execution_frequency
                      : "-"}
                  </Text>
                </View>
                <View
                  style={[
                    styles.resourceItem,
                    { backgroundColor: themeColors.sectionBg },
                  ]}
                >
                  <Text
                    style={[
                      styles.resourceLabel,
                      { color: themeColors.textMuted },
                    ]}
                  >
                    {t("subProcesses.executionEmployeesCount")}
                  </Text>
                  <Text
                    style={[
                      styles.resourceValue,
                      { color: themeColors.textPrimary },
                    ]}
                  >
                    {subProcess.employees_count || "-"}
                  </Text>
                </View>
              </View>

              <View
                style={[
                  styles.resourcesRow,
                  isSmallScreen && styles.resourcesRowSmall,
                ]}
              >
                <View
                  style={[
                    styles.resourceItem,
                    { backgroundColor: themeColors.sectionBg },
                  ]}
                >
                  <Text
                    style={[
                      styles.resourceLabel,
                      { color: themeColors.textMuted },
                    ]}
                  >
                    {t("subProcesses.processCost")}
                  </Text>
                  <Text
                    style={[
                      styles.resourceValue,
                      { color: themeColors.textPrimary },
                    ]}
                  >
                    {subProcess.cost ? `${subProcess.cost}` : "-"}
                  </Text>
                </View>
                <View
                  style={[
                    styles.resourceItem,
                    { backgroundColor: themeColors.sectionBg },
                  ]}
                >
                  <Text
                    style={[
                      styles.resourceLabel,
                      { color: themeColors.textMuted },
                    ]}
                  >
                    {t("subProcesses.approvalPerson")}
                  </Text>
                  <Text
                    style={[
                      styles.resourceValue,
                      { color: themeColors.textPrimary },
                    ]}
                  >
                    {subProcess.approval_person || "-"}
                  </Text>
                </View>
              </View>
            </View>
          </View>

          {/* Process Steps Section - خطوات العملية */}
          <View
            style={[
              styles.stepsSection,
              {
                backgroundColor: themeColors.card,
                borderColor: themeColors.cardBorder,
              },
            ]}
          >
            <Text
              style={[
                styles.stepsSectionTitle,
                { color: themeColors.textPrimary },
              ]}
            >
              {t("subProcesses.processSteps")}
            </Text>

            {steps.length > 0 ? (
              steps.map((step) => (
                <View
                  key={step.id}
                  style={[
                    styles.stepCard,
                    {
                      backgroundColor: themeColors.sectionBg,
                      borderColor: themeColors.cardBorder,
                    },
                  ]}
                >
                  <View style={styles.stepCardContent}>
                    {/* Step Header */}
                    <View style={styles.stepHeader}>
                      <Text
                        style={[
                          styles.stepTitle,
                          { color: themeColors.textPrimary },
                        ]}
                      >
                        {translate(step.name)}
                      </Text>
                      <View style={styles.stepMeta}>
                        {step.duration && (
                          <View style={styles.stepMetaItem}>
                            <Text
                              style={[
                                styles.stepMetaText,
                                { color: themeColors.textMuted },
                              ]}
                            >
                              {translate(step.duration)}
                            </Text>
                            <ClockIcon
                              size={14}
                              color={themeColors.textMuted}
                            />
                          </View>
                        )}
                        {step.responsible && (
                          <View style={styles.stepMetaItem}>
                            <Text
                              style={[
                                styles.stepMetaText,
                                { color: themeColors.textMuted },
                              ]}
                            >
                              {translate(step.responsible)}
                            </Text>
                            <UserIcon size={14} color={themeColors.textMuted} />
                          </View>
                        )}
                      </View>
                    </View>

                    {/* Step Description */}
                    {step.description && (
                      <View style={styles.stepDescriptionRow}>
                        <Text
                          style={[
                            styles.stepDescriptionLabel,
                            { color: themeColors.textMuted },
                          ]}
                        >
                          {t("common.description")}:
                        </Text>
                        <Text
                          style={[
                            styles.stepDescriptionText,
                            { color: themeColors.textSecondary },
                          ]}
                        >
                          {translate(step.description)}
                        </Text>
                      </View>
                    )}

                    {/* Step Tools */}
                    {step.tools && (
                      <View style={styles.stepToolsRow}>
                        <Text
                          style={[
                            styles.stepToolsLabel,
                            { color: themeColors.textMuted },
                          ]}
                        >
                          {t("subProcesses.tools")}:
                        </Text>
                        <Text
                          style={[
                            styles.stepToolsText,
                            { color: themeColors.textSecondary },
                          ]}
                        >
                          {translate(step.tools)}
                        </Text>
                      </View>
                    )}

                    {/* Organizational Units (ManyToMany) */}
                    {step.organizational_units_details &&
                      step.organizational_units_details.length > 0 && (
                        <View style={styles.stepAssignmentRow}>
                          <View style={styles.stepAssignmentHeader}>
                            <BuildingIcon size={14} color={tokens.colors.raw.blue["500"]} />
                            <Text
                              style={[
                                styles.stepAssignmentLabel,
                                { color: themeColors.textMuted },
                              ]}
                            >
                              {t("subProcesses.responsibleUnits")}:
                            </Text>
                          </View>
                          <View style={styles.stepTagsContainer}>
                            {step.organizational_units_details.map((unit) => (
                              <View key={unit.id} style={styles.stepUnitTag}>
                                <Text style={styles.stepUnitTagText}>
                                  {translate(unit.name_ar || unit.name)}
                                </Text>
                              </View>
                            ))}
                          </View>
                        </View>
                      )}

                    {/* Responsible Employees (ManyToMany) */}
                    {step.responsible_employees_details &&
                      step.responsible_employees_details.length > 0 && (
                        <View style={styles.stepAssignmentRow}>
                          <View style={styles.stepAssignmentHeader}>
                            <UsersIcon size={14} color={tokens.colors.raw.green["500"]} />
                            <Text
                              style={[
                                styles.stepAssignmentLabel,
                                { color: themeColors.textMuted },
                              ]}
                            >
                              {t("subProcesses.responsibleEmployees")}:
                            </Text>
                          </View>
                          <View style={styles.stepTagsContainer}>
                            {step.responsible_employees_details.map((emp) => (
                              <View key={emp.id} style={styles.stepEmployeeTag}>
                                <Text style={styles.stepEmployeeTagText}>
                                  {translate(emp.name_ar || emp.name_en)}
                                  {emp.position && (
                                    <Text style={styles.stepEmployeePosition}>
                                      {" "}
                                      ({translate(emp.position)})
                                    </Text>
                                  )}
                                </Text>
                              </View>
                            ))}
                          </View>
                        </View>
                      )}
                  </View>

                  {/* Step Number */}
                  <View style={styles.stepNumberContainer}>
                    <Text style={styles.stepNumber}>{step.order}</Text>
                  </View>
                </View>
              ))
            ) : (
              <View style={styles.emptyStateContainer}>
                <Text
                  style={[
                    styles.emptyStateText,
                    { color: themeColors.textMuted },
                  ]}
                >
                  {t("subProcesses.noSteps")}
                </Text>
              </View>
            )}
          </View>

          {/* Process Flow Diagram Section - المسار التدفقي للعملية */}
          {subProcess.process_flow_diagram && (
            <View
              style={[
                styles.fileSection,
                {
                  backgroundColor: themeColors.card,
                  borderColor: themeColors.cardBorder,
                },
              ]}
            >
              <Text
                style={[
                  styles.fileSectionTitle,
                  { color: themeColors.textPrimary },
                ]}
              >
                {t("subProcesses.processFlowImages")}
              </Text>

              {/* Image Display */}
              <TouchableOpacity
                onPress={() => {
                  const fileUrl = subProcess.process_flow_diagram?.startsWith(
                    "http",
                  )
                    ? subProcess.process_flow_diagram
                    : `${API_BASE_HOST}${subProcess.process_flow_diagram}`;
                  Linking.openURL(fileUrl);
                }}
                activeOpacity={0.8}
              >
                <Image
                  source={{
                    uri: subProcess.process_flow_diagram?.startsWith("http")
                      ? subProcess.process_flow_diagram
                      : `${API_BASE_HOST}${subProcess.process_flow_diagram}`,
                  }}
                  style={styles.processFlowImage}
                  resizeMode="contain"
                />
              </TouchableOpacity>

              {/* Download/View Button */}
              <TouchableOpacity
                style={[
                  styles.fileDownloadButton,
                  {
                    backgroundColor: themeColors.sectionBg,
                    borderColor: themeColors.cardBorder,
                  },
                ]}
                onPress={() => {
                  const fileUrl = subProcess.process_flow_diagram?.startsWith(
                    "http",
                  )
                    ? subProcess.process_flow_diagram
                    : `${API_BASE_HOST}${subProcess.process_flow_diagram}`;
                  Linking.openURL(fileUrl);
                }}
              >
                <FileIcon size={20} color={themeColors.brand} />
                <Text
                  style={[
                    styles.fileDownloadText,
                    { color: themeColors.textPrimary },
                  ]}
                >
                  {t("common.viewFullSize")}
                </Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
        <EvidenceTable
          relatedType="sub_process"
          filterRelatedId={id}
          title={t('evidence.sectionTitle')}
          canManage={canManageEvidence}
          showOrgColumn={canViewAllEvidence}
          onAddPress={() => navigation.navigate('AddEvidence', {
            relatedType: 'sub_process', relatedId: id, contextLabel: t('subProcesses.title'),
          })}
          onEditPress={(ev) => navigation.navigate('AddEvidence', {
            evidenceId: ev.id, relatedType: ev.related_type, relatedId: ev.related_id, contextLabel: t('subProcesses.title'),
          })}
        />
      </ScrollView>

      {/* Deletion Impact Modal */}
      <DeletionImpactModal
        visible={showDeletionImpact}
        onClose={() => setShowDeletionImpact(false)}
        onConfirm={confirmDelete}
        entityName={subProcess?.name || ''}
        fetchImpact={async () => {
          const response = await subProcessesApi.deletionImpact(id);
          return response.data;
        }}
        isOrgEmployee={isOrgEmployee}
        isSubmitting={isDeleting}
      />

    </PageContainer>
  );
}

// ============================================================
// Styles
// ============================================================

const getStyles = (isRTL: boolean) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FAFAFA',
  },
  content: {
    padding: tokens.spacing.absolute["6"],
    paddingTop: tokens.spacing.absolute["4"],
    gap: tokens.spacing.absolute["6"],
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: tokens.spacing.absolute["4"],
  },
  loadingText: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.slate["500"],
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: tokens.spacing.absolute["4"],
  },
  errorText: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.red["500"],
  },
  backButton: {
    backgroundColor: COLORS.primary,
    paddingHorizontal: tokens.spacing.absolute["6"],
    paddingVertical: tokens.spacing.absolute["3"],
    borderRadius: tokens.radii.absolute["radius-8"],
  },
  backButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.white,
  },

  // Breadcrumb
  breadcrumb: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    marginBottom: tokens.spacing.absolute["4"],
    gap: tokens.spacing.absolute["2"],
  },
  breadcrumbItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  breadcrumbText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
  },
  breadcrumbActive: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: COLORS.primary,
  },

  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["5"],
  },

  // Main Content Row
  mainContentRow: {
    flexDirection: 'row-reverse',
    gap: tokens.spacing.absolute["6"],
  },
  mainContentRowSmall: {
    flexDirection: 'column',
  },

  // Basic Info Section
  basicInfoSection: {
    flex: 1,
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
    padding: tokens.spacing.absolute["6"],
  },
  basicInfoSectionSmall: {
    width: '100%',
  },
  basicInfoTitle: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["5"],
  },
  infoGrid: {
    gap: tokens.spacing.absolute["4"],
  },
  infoRow: {
    flexDirection: 'row-reverse',
    gap: tokens.spacing.absolute["6"],
  },
  infoItem: {
    flex: 1,
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["4"],
  },
  infoLabel: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["2"],
  },
  infoValue: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },

  // Donut Section
  donutSection: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
    padding: tokens.spacing.absolute["6"],
    alignItems: 'center',
    width: 240,
  },
  donutSectionSmall: {
    width: '100%',
  },
  sectionLabel: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    marginBottom: tokens.spacing.absolute["5"],
  },
  donutLegendItems: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: tokens.spacing.absolute["3"],
    marginTop: tokens.spacing.absolute["4"],
  },
  donutLegendRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1_5"],
  },
  donutLegendDot: {
    width: 10,
    height: 10,
    borderRadius: tokens.radii.absolute["radius-5"],
  },
  donutLegendLabel: {
    fontSize: tokens.typography.paragraph["paragraph caption font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
  },

  // Resources Section - الموارد والتنفيذ
  resourcesSection: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
    padding: tokens.spacing.absolute["6"],
  },
  resourcesSectionTitle: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["5"],
  },
  resourcesGrid: {
    gap: tokens.spacing.absolute["4"],
  },
  resourcesRow: {
    flexDirection: 'row-reverse',
    gap: tokens.spacing.absolute["6"],
  },
  resourcesRowSmall: {
    flexDirection: 'column',
    gap: tokens.spacing.absolute["4"],
  },
  resourceItem: {
    flex: 1,
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["4"],
  },
  resourceLabel: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["2"],
  },
  resourceValue: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },

  // File Section - المسار التدفقي للعملية
  fileSection: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
    padding: tokens.spacing.absolute["6"],
    marginBottom: tokens.spacing.absolute["4"],
  },
  fileSectionTitle: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["4"],
  },
  processFlowImage: {
    width: '100%',
    height: 400,
    borderRadius: tokens.radii.absolute["radius-8"],
    marginBottom: tokens.spacing.absolute["4"],
    backgroundColor: tokens.colors.raw.gray["50"],
  },
  fileDownloadButton: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacing.absolute["3"],
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
    padding: tokens.spacing.absolute["4"],
  },
  fileDownloadText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: 'center',
  },

  // Steps Section - خطوات العملية
  stepsSection: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
    padding: tokens.spacing.absolute["6"],
  },
  stepsSectionTitle: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["5"],
  },
  stepCard: {
    flexDirection: 'row',
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
    marginBottom: tokens.spacing.absolute["4"],
    borderEndWidth: 4,
    borderEndColor: tokens.colors.raw.slate["300"],
    overflow: 'hidden',
  },
  stepCardContent: {
    flex: 1,
    padding: tokens.spacing.absolute["4"],
    gap: tokens.spacing.absolute["3"],
  },
  stepHeader: {
    gap: tokens.spacing.absolute["2"],
  },
  stepTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  stepMeta: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: tokens.spacing.absolute["4"],
  },
  stepMetaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1_5"],
  },
  stepMetaText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.slate["500"],
  },
  stepDescriptionRow: {
    flexDirection: 'row-reverse',
    gap: tokens.spacing.absolute["2"],
  },
  stepDescriptionLabel: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.slate["500"],
  },
  stepDescriptionText: {
    flex: 1,
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    lineHeight: 20,
  },
  stepToolsRow: {
    flexDirection: 'row-reverse',
    gap: tokens.spacing.absolute["2"],
  },
  stepToolsLabel: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.slate["500"],
  },
  stepToolsText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  // Step Assignment Styles (for ManyToMany display)
  stepAssignmentRow: {
    gap: tokens.spacing.absolute["2"],
    paddingTop: tokens.spacing.absolute["2"],
    borderTopWidth: tokens.spacing.absolute["px"],
    borderTopColor: tokens.colors.raw.slate["200"],
  },
  stepAssignmentHeader: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1_5"],
  },
  stepAssignmentLabel: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.slate["500"],
  },
  stepTagsContainer: {
    flexDirection: 'row-reverse',
    flexWrap: 'wrap',
    gap: tokens.spacing.absolute["2"],
  },
  stepUnitTag: {
    backgroundColor: '#EFF6FF',
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: '#BFDBFE',
    borderRadius: tokens.radii.absolute["radius-16"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["1"],
  },
  stepUnitTagText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: '#1D4ED8',
    textAlign: getTextAlign(isRTL),
  },
  stepEmployeeTag: {
    backgroundColor: '#F0FDF4',
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: '#BBF7D0',
    borderRadius: tokens.radii.absolute["radius-16"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["1"],
  },
  stepEmployeeTagText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: '#166534',
    textAlign: getTextAlign(isRTL),
  },
  stepEmployeePosition: {
    fontSize: tokens.typography.paragraph["paragraph caption font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
  },
  stepNumberContainer: {
    width: 48,
    backgroundColor: tokens.colors.raw.slate["200"],
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepNumber: {
    fontSize: tokens.typography["heading 3"]["font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
  },

  // KPIs Section
  kpisSection: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
    padding: tokens.spacing.absolute["6"],
  },
  kpisSectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: tokens.spacing.absolute["4"],
  },
  kpisSectionTitle: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
  },
  addKpiBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["300"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: tokens.spacing.absolute["2"],
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  addKpiBtnText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
  },

  // KPI Cards Grid
  // Horizontal scroll container for KPI cards
  horizontalScrollContainer: {
    flexGrow: 0,
  },
  // RTL wrapper - flips the entire ScrollView horizontally so cards start from right
  rtlWrapper: {
    transform: [{ scaleX: -1 }],
  },
  // RTL card - flips each card back to normal orientation
  rtlCard: {
    transform: [{ scaleX: -1 }],
  },
  horizontalCardsRow: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["4"],
    paddingHorizontal: tokens.spacing.absolute["1"],
    paddingBottom: tokens.spacing.absolute["3"],
  },
  kpiCardNew: {
    width: 288, // Fixed width (w-72)
    flexShrink: 0, // Prevent shrinking
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["4"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    gap: tokens.spacing.absolute["4"],
  },
  kpiCardContent: {
    gap: tokens.spacing.absolute["4"],
  },
  kpiCardTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    minHeight: 40,
  },
  kpiProgressRow: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  kpiProgressBadge: {
    backgroundColor: tokens.colors.raw.slate["100"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["0_5"],
    borderRadius: 100,
  },
  kpiProgressBadgeText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: 'center',
  },
  kpiProgressBarContainer: {
    flex: 1,
    height: 8,
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
    position: 'relative',
    overflow: 'visible',
    flexDirection: 'row-reverse',
  },
  kpiProgressBarFill: {
    height: 8,
    borderRadius: tokens.radii.absolute["radius-8"],
  },
  kpiProgressDot: {
    position: 'absolute',
    top: -3,
    width: 14,
    height: 14,
    borderRadius: tokens.radii.absolute["radius-7"],
    borderWidth: 2,
    borderColor: '#FCFCFC',
  },
  kpiViewDetailsBtn: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: 3,
  },
  kpiViewDetailsBtnText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },

  // Empty State
  emptyStateContainer: {
    alignItems: 'center',
    paddingVertical: tokens.spacing.absolute["8"],
    gap: tokens.spacing.absolute["4"],
  },
  emptyStateText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.slate["500"],
  },
  addKpiEmptyBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["300"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: tokens.spacing.absolute["2"],
  },
  addKpiEmptyBtnText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
  },
});

const styles = getStyles(true);
